<template>
  <div class="books-container">
    <section class="books-hero">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <span class="hero-label">Publications</span>
        <h1 class="hero-title">Published Works</h1>
        <p class="hero-description">
          Deep investigative research into the power structures shaping modern society —
          from corporate imperialism to industrial complexes
        </p>
        <div class="hero-stats">
          <div class="stat-box">
            <span class="stat-number">{{ books.length + benevolismSeries.length + covenantSeries.length }}</span>
            <span class="stat-text">Books Published</span>
          </div>
          <div class="stat-box">
            <span class="stat-number">{{ benevolismSeries.length + covenantSeries.length }}</span>
            <span class="stat-text">Series Volumes</span>
          </div>
          <div class="stat-box">
            <span class="stat-number">2026</span>
            <span class="stat-text">Latest Release</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ── THE BENEVOLISM SERIES ──────────────────────────────── -->
    <section class="series-section">
      <div class="series-inner">
        <div class="series-header">
          <div class="series-header-left">
            <div class="series-mark">B</div>
            <div>
              <div class="series-eyebrow">Ongoing Series</div>
              <h2 class="series-title">The Benevolism Series</h2>
              <p class="series-sub">
                A growing body of work applying the Benevolism philosophy — that prosperity grows when businesses take less — to the specific domains where extraction causes the most harm.
              </p>
            </div>
          </div>
          <div class="series-vol-count">
            <span class="series-vol-num">{{ benevolismSeries.length }}</span>
            <span class="series-vol-label">Volumes Published</span>
          </div>
        </div>

        <div class="series-grid">
          <div
            v-for="(vol, index) in benevolismSeries"
            :key="vol.title"
            class="series-card"
          >
            <div class="series-cover-wrap">
              <div class="series-cover" :class="vol.theme">
                <div class="sc-crown">♛</div>
                <div class="sc-title">{{ vol.titleLine1 }}<br><strong>{{ vol.titleLine2 }}</strong></div>
                <div class="sc-divider"></div>
                <div class="sc-scales">⚖</div>
                <div class="sc-divider"></div>
                <div class="sc-series">The Benevolism Series — Volume {{ vol.volume }}</div>
                <div class="sc-author">ENRICO MICHAEL SEVERE</div>
                <div class="sc-heir">Davidic Heir, Son of King David</div>
              </div>
              <div class="sc-badge">VOL. {{ vol.volume }}</div>
            </div>

            <div class="series-card-content">
              <div class="series-vol-tag">Volume {{ vol.volume }}</div>
              <h3 class="series-card-title">{{ vol.title }}</h3>
              <p class="series-card-sub">{{ vol.subtitle }}</p>
              <p class="series-card-desc">{{ vol.description }}</p>

              <div class="series-card-topics" v-if="vol.topics">
                <span v-for="t in vol.topics" :key="t" class="series-topic-tag">{{ t }}</span>
              </div>

              <div class="series-card-highlights" v-if="vol.highlights">
                <ul class="series-highlights-list">
                  <li v-for="h in vol.highlights" :key="h">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {{ h }}
                  </li>
                </ul>
              </div>

              <a v-if="!vol.comingSoon" :href="vol.link" target="_blank" class="series-cta">
                <span>Buy on Amazon</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
              <span v-else class="series-cta series-cta--soon">Coming Soon</span>
            </div>
          </div>
        </div>

        <div class="series-footer">
          <span class="series-footer-note">Fourteen volumes published — public policy, housing, transportation, energy, food, labor, retail, entertainment &amp; real estate — with more forthcoming.</span>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="series-footer-link">
            Explore the full series at benevolism.com →
          </a>
        </div>
      </div>
    </section>

    <!-- ── THE COVENANT SERIES ────────────────────────────────── -->
    <section class="series-section covenant-series-section">
      <div class="series-inner">
        <div class="series-header">
          <div class="series-header-left">
            <div class="series-mark series-mark--covenant">C</div>
            <div>
              <div class="series-eyebrow">A Global Application</div>
              <h2 class="series-title">The Covenant Series</h2>
              <p class="series-sub">
                Where the Benevolism Series builds the domestic doctrine, the Covenant Series takes Benevolism to international scope — a documented indictment and a covenant redesign, nation by nation, continent by continent.
              </p>
            </div>
          </div>
          <div class="series-vol-count">
            <span class="series-vol-num">{{ covenantSeries.length }}</span>
            <span class="series-vol-label">Volumes Published</span>
          </div>
        </div>

        <div class="series-grid">
          <div
            v-for="(vol, index) in covenantSeries"
            :key="vol.title"
            class="series-card covenant-card"
          >
            <div class="series-cover-wrap">
              <img :src="vol.cover" :alt="vol.title" class="covenant-cover-img" />
            </div>

            <div class="series-card-content">
              <div class="series-vol-tag">{{ vol.region }}</div>
              <h3 class="series-card-title">{{ vol.title }}</h3>
              <p class="series-card-sub">{{ vol.subtitle }}</p>
              <p class="series-card-desc">{{ vol.description }}</p>

              <div class="series-card-topics" v-if="vol.topics">
                <span v-for="t in vol.topics" :key="t" class="series-topic-tag">{{ t }}</span>
              </div>

              <div class="series-card-highlights" v-if="vol.highlights">
                <ul class="series-highlights-list">
                  <li v-for="h in vol.highlights" :key="h">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {{ h }}
                  </li>
                </ul>
              </div>

              <a :href="vol.link" target="_blank" class="series-cta">
                <span>Buy on Amazon</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div class="series-footer">
          <span class="series-footer-note">Applying the covenant redesign continent by continent — more volumes forthcoming.</span>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="series-footer-link">
            Explore Benevolism at benevolism.com →
          </a>
        </div>
      </div>
    </section>

    <!-- ── ALSO PUBLISHED ────────────────────────────────────── -->
    <section class="books-section">
      <div class="also-header">
        <div class="also-rule"></div>
        <span class="also-label">Also Published</span>
        <div class="also-rule"></div>
      </div>
      <div class="books-grid">
        <div
          v-for="(book, index) in books"
          :key="book.title"
          class="book-card"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="book-cover-wrapper">
            <div class="book-cover-container">
              <img :src="book.cover" :alt="book.title" class="book-cover-image" />
              <div class="book-shine"></div>
            </div>
            <div class="book-badge" v-if="book.badge">{{ book.badge }}</div>
          </div>

          <div class="book-content">
            <div class="book-header">
              <h2 class="book-title">{{ book.title }}</h2>
              <p class="book-author">by {{ book.author }}</p>
            </div>

            <p class="book-description">{{ book.description }}</p>

            <div class="book-topics" v-if="book.topics">
              <div class="topic-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                Key Topics:
              </div>
              <div class="topics-list">
                <span v-for="topic in book.topics" :key="topic" class="topic-tag">{{ topic }}</span>
              </div>
            </div>

            <div class="book-highlights" v-if="book.highlights">
              <ul class="highlights-list">
                <li v-for="highlight in book.highlights" :key="highlight">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  {{ highlight }}
                </li>
              </ul>
            </div>

            <a :href="book.link" target="_blank" class="book-cta">
              <span>View on Amazon</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Author Bio Section -->
    <section class="author-bio-section">
      <div class="bio-content">
        <div class="bio-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          </svg>
        </div>
        <h2 class="bio-title">About the Author</h2>
        <p class="bio-text">
          Enrico Michael Severe is a private researcher and the founder of Benevolism — a modern economic philosophy built on the belief that prosperity grows when businesses take less from the people they serve. His work spans corporate power analysis, criminal justice, military policy, and economic philosophy.
        </p>
        <p class="bio-text">
          Through meticulous research and clear prose, his books illuminate the often-hidden mechanisms of power — from planned obsolescence to the prison-industrial complex — while his Benevolism Series builds a rigorous framework for economic systems designed to serve people rather than extract from them.
        </p>
        <div class="bio-cta">
          <router-link to="/about" class="bio-link">
            Learn More About Enrico
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </router-link>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="bio-link bio-link--gold">
            Explore Benevolism
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </a>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="newsletter-section">
      <div class="newsletter-content">
        <div class="newsletter-icon">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
        </div>
        <h2 class="newsletter-title">Stay Informed</h2>
        <p class="newsletter-description">
          Explore more research and insights on the blog, or discover the economic philosophy guiding this work
        </p>
        <div class="newsletter-buttons">
          <router-link to="/blog" class="newsletter-btn primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            Read the Blog
          </router-link>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="newsletter-btn gold">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
            </svg>
            Explore Benevolism
          </a>
          <router-link to="/about" class="newsletter-btn secondary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            About Enrico
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'BooksView',
  data () {
    return {
      /* ── BENEVOLISM SERIES ──────────────────────────────────────
         Add Volume III, IV etc. here — everything else auto-updates.
      ─────────────────────────────────────────────────────────── */
      benevolismSeries: [
        {
          volume: 'I',
          theme: 'theme-navy',
          title: 'The Benevolent Economy',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT ECONOMY',
          subtitle: 'How Wealth Grows When Businesses Take Less',
          description: 'The founding treatise of the Benevolism movement. Six parts, sixteen chapters, and a complete doctrine for economic design that prioritizes participation, fairness, and shared prosperity over extraction.',
          topics: ['Economic Philosophy', 'Pricing Design', 'Participatory Markets', 'Shared Prosperity'],
          highlights: [
            'Introduces the Five Core Convictions of Benevolism',
            'Codifies the Ten Laws of the Benevolent Economy',
            'Presents the Benevolent Pledge for entrepreneurs and consumers',
          ],
          link: 'https://www.amazon.com/dp/B0H5G63GSY/ref=sr_1_4?dib=eyJ2IjoiMSJ9.xMDvKYz98NooQvexXqAs9huL2IyDJNN1b-3W6tSzLRAR3QcukjPmlarBs4IruOLFIIgvAvHYyE62O1qCvO64XsX60bNRX7O3hfqRgYZ_v0QvSdecE2qOX2hI3eAWZL_0TvZwShE2vhKH9g9ljxY204wY-ke7wsd_gn0SL7M1qOw.ALZ-FH-QsOxJeyi6kYohTXLOZt0-FKXH6qK-XhllyZw&dib_tag=se&qid=1781604933&refinements=p_27%3AEnrico++Michael+Severe&s=books&sr=1-4&text=Enrico++Michael+Severe',
        },
        {
          volume: 'II',
          theme: 'theme-emerald',
          title: 'The Benevolent Cure',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT CURE',
          subtitle: 'How Benevolism Would Transform Healthcare, Restore Affordability, and Heal the People the System Has Abandoned',
          description: 'Four structural layers, ten binding laws, and a five-part Healthcare Pledge for transforming a system built on extraction into one built for healing. Because in healthcare, we are all, eventually, patients.',
          topics: ['Healthcare Economics', 'Pharmaceutical Pricing', 'Universal Access', 'Insurance Reform'],
          highlights: [
            'Diagnoses the five primary mechanisms of healthcare extraction',
            'Introduces the Ten Laws of Benevolent Healthcare',
            'Prescribes the Healthcare Pledge for providers, institutions, and policymakers',
          ],
          link: 'https://www.amazon.com/dp/B0H5YML4DJ/ref=sr_1_5?dib=eyJ2IjoiMSJ9.RAhaHUXT97zeaeHcYzHnjCXe1nWSGCDMVaBhfVR_V7Xa8fTZ-Q8Xc2ICg_riCD_WCwx-S04VHrhmcpettJ-291JpdBfpPbvObYOJJsbCuaebALg9s_FT2gJMkoDpARYNsesEbJNbYL5sefKsBIVXTzFEi1qclI8w1I2rx3aHbQgyEehm2JKurjqqiyW8Q2Ee.v_2ounSwhDwLiUIPwWAYWFnAGupsQY_CFNnk0SJilmA&dib_tag=se&qid=1781827395&refinements=p_27%3AEnrico+Michael+Severe&s=books&sr=1-5&text=Enrico+Michael+Severe',
        },
        {
          volume: 'III',
          theme: 'theme-bronze',
          title: 'The Benevolent Bank',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT BANK',
          subtitle: 'How Benevolism Would Transform Finance, Democratize Wealth, and Free Millions From the Machinery of Financial Extraction',
          description: 'Five founding principles, ten binding laws, and a five-part Financial Pledge for transforming a financial system built on extraction into one built to serve the people who depend on it — because almost everyone has a bank account, carries debt, or is trying to save for the future.',
          topics: ['Financial Extraction', 'Banking Reform', 'Predatory Lending', 'Investment Fees'],
          highlights: [
            'Diagnoses the architecture of financial extraction, from fee structures to the poverty premium',
            'Introduces the Ten Laws of Benevolent Finance',
            'Prescribes the Financial Pledge for institutions, professionals, investors, and policymakers',
          ],
          link: 'https://www.amazon.com/BENEVOLENT-BANK-Benevolism-Democratize-Extraction/dp/B0H6M1Z43C/ref=sr_1_9?dib=eyJ2IjoiMSJ9.nAnangaUMLezmb8lJpTvVKIodBo_EwH5MSfQBHF7mvKtD9ke4FIebNJ4WYkdOyRpKVBuHMf6eRYQiXAhfJwETMKcd4BN9nsN6Ex9ckWGUT54tYB-tuua5aTr6z1TgZpkbroq6cHQrHm5skAvpMAYjeMZZLmz5qYZbosMkmfBPindSP9HThWGvWt5obOt_nMDZGQzwHkX933XggJR8-DFKIfNM8bvWULYY3aZw5-Qjoc.PMLmXTpK8fi2uzSiB0IOdFsRkK6EibXkqpUhJCRIBIE&dib_tag=se&qid=1784202163&refinements=p_27%3AEnrico+Michael+Severe&s=books&sr=1-9&text=Enrico+Michael+Severe',
        },
        {
          volume: 'IV',
          theme: 'theme-violet',
          title: 'The Benevolent Classroom',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT CLASSROOM',
          subtitle: 'How Benevolism Would Transform Education, End the Tuition Crisis, and Make Learning a Right for Every Human Being',
          description: 'Five founding principles, ten binding laws, and a five-part Education Pledge for transforming an education system built on extraction into one built for learning — because education is where everything else begins.',
          topics: ['Education Economics', 'Tuition Reform', 'Student Debt', 'Educational Equity'],
          highlights: [
            'Diagnoses the tuition spiral, credential inflation, and the equity chasm in higher education',
            'Introduces the Ten Laws of Benevolent Education',
            'Prescribes the Education Pledge for educators, institutions, policymakers, and students',
          ],
          link: 'https://www.amazon.com/dp/B0H93BGQPK/ref=sr_1_6?dib=eyJ2IjoiMSJ9.nAnangaUMLezmb8lJpTvVKIodBo_EwH5MSfQBHF7mvKtD9ke4FIebNJ4WYkdOyRpKVBuHMf6eRYQiXAhfJwETMKcd4BN9nsN6Ex9ckWGUT54tYB-tuua5aTr6z1TgZpkbroq6cHQrHm5skAvpMAYjeMZZLmz5qYZbosMkmfBPindSP9HThWGvWt5obOt_nMDZGQzwHkX933XggJR8-DFKIfNM8bvWULYY3aZw5-Qjoc.PMLmXTpK8fi2uzSiB0IOdFsRkK6EibXkqpUhJCRIBIE&dib_tag=se&qid=1784202163&refinements=p_27%3AEnrico+Michael+Severe&s=books&sr=1-6&text=Enrico+Michael+Severe',
        },
        {
          volume: 'V',
          theme: 'theme-steel',
          title: 'The Benevolent Machine',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT MACHINE',
          subtitle: 'How Benevolism Would Transform Technology, Reclaim the Internet, and Build AI That Serves Humanity',
          description: 'Five principles of benevolent platform design, ten binding laws, and a five-part Technology Pledge for transforming a technology sector built on the extraction of attention and data into one built to serve the people who use it — because the machines being built right now will shape human civilization for generations.',
          topics: ['Surveillance Capitalism', 'Algorithmic Accountability', 'Data Sovereignty', 'AI Alignment'],
          highlights: [
            'Diagnoses the attention economy, platform monopoly, and the surveillance business model of the internet',
            'Introduces the Ten Laws of Benevolent Technology',
            'Prescribes the Technology Pledge for technologists, organizations, investors, users, and society',
          ],
          link: 'https://www.amazon.com/dp/B0H9GRP117/ref=sr_1_6?dib=eyJ2IjoiMSJ9.3vO5FTKeYX3m5Fi9Jlxqkotuc4o3FSqlJhG-J3tQIceS5yk6HOlA5YjbfHg840PXqhOhXbvv3D1GCvsgrKS-dhDZheFrh6d8O_awtQ-ICWmSri2KpRbA_u-OCYLBbNFznKXelt17jZSVHv9DQUu8oUGEDyrxZJCXcb4H_Ds7Wu3Vpj5s1xnlp6fKCU3JUxxQiLb6M1os20x-iVclJm_CqJJ_-MaxDV7OWqkrDXnUQVA.5mZCdkHQUi1XkwGoFhtIyshq92Pmw2Pl-K4zDFQ-lXc&dib_tag=se&qid=1784463372&refinements=p_27%3AEnrico%2BMichael%2BSevere&s=books&sr=1-6&text=Enrico%2BMichael%2BSevere',
        },
        {
          volume: 'VI',
          theme: 'theme-crimson',
          title: 'The Benevolent State',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT STATE',
          subtitle: 'How Benevolism Would Transform Public Policy, Rebuild the Social Contract, and Create a Government That Serves Everyone',
          description: 'A diagnosis of how taxation, regulation, and welfare have been captured by extraction logic — and a full doctrine for a government designed to serve the people it governs, not the interests that govern through it.',
          topics: ['Tax Policy', 'Regulatory Capture', 'Welfare Reform', 'Universal Basic Income'],
          highlights: [
            'Diagnoses how taxation, regulation, and welfare have been captured by extraction logic',
            'Introduces the Ten Laws of Benevolent Governance',
            'Prescribes the Civic Pledge for policymakers, civil servants, institutions, and society',
          ],
          link: 'https://www.amazon.com/dp/B0HCF58PJ1',
        },
        {
          volume: 'VII',
          theme: 'theme-teal',
          title: 'The Benevolent Home',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT HOME',
          subtitle: 'How Benevolism Would End the Housing Crisis, Make Shelter Affordable for Every Family, and Build Communities That Last',
          description: 'Housing is the physical foundation of a human life, not an investment vehicle first and shelter second. A full prescription for supply, ownership, and tenant justice that puts shelter before speculation.',
          topics: ['Housing Affordability', 'Zoning Reform', 'Community Land Trusts', 'Tenant Rights'],
          highlights: [
            'Diagnoses the financialization of housing and the manufactured scarcity of supply',
            'Introduces the Ten Laws of Benevolent Housing',
            'Prescribes the Housing Pledge for providers, developers, policymakers, and society',
          ],
          link: 'https://www.amazon.com/dp/B0HCDVBFHK',
        },
        {
          volume: 'VIII',
          theme: 'theme-amber',
          title: 'The Benevolent Journey',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT JOURNEY',
          subtitle: 'How Benevolism Would Transform Transportation, Free People From Car Dependency, and Build Mobility as a Public Right',
          description: 'Transportation is the infrastructure of opportunity. A full doctrine for transit, pricing, and land use that moves everyone toward the lives they deserve to live, not just those who can afford a car.',
          topics: ['Public Transit', 'Car Dependency', 'Urban Mobility', 'Transportation Equity'],
          highlights: [
            'Diagnoses how transportation policy was captured by the automobile industrial complex',
            'Introduces the Ten Laws of Benevolent Transportation',
            'Prescribes the Mobility Pledge for professionals, companies, policymakers, and society',
          ],
          link: 'https://www.amazon.com/dp/B0HDQVQNZJ',
        },
        {
          volume: 'IX',
          theme: 'theme-cyan',
          title: 'The Benevolent Grid',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT GRID',
          subtitle: 'How Benevolism Would Transform Energy, Democratize Renewable Power, and Make Clean Energy a Right for Every Person',
          description: 'Energy is the condition for everything else. A full doctrine for utility regulation, renewable democratization, and honest pricing that turns the fastest cost decline in energy history into lower bills for everyone.',
          topics: ['Energy Policy', 'Utility Regulation', 'Renewable Democratization', 'Energy Poverty'],
          highlights: [
            'Diagnoses how utility monopolies and fossil fuel subsidies extract from ratepayers',
            'Introduces the Ten Laws of Benevolent Energy',
            'Prescribes the Energy Pledge for utilities, professionals, policymakers, and society',
          ],
          link: 'https://www.amazon.com/dp/B0HFD5XTTT',
        },
        {
          volume: 'X',
          theme: 'theme-olive',
          title: 'The Benevolent Harvest',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT HARVEST',
          subtitle: 'How Benevolism Would Transform Food Systems, End Grocery Extraction, and Guarantee Nourishing Food for Every Person',
          description: 'The world produces enough food to feed everyone — the failure is distribution, not production. A full doctrine for farm-to-fork fairness, from farmer income to grocery pricing to school meals.',
          topics: ['Food Systems', 'Farm Income', 'Grocery Pricing', 'Food Deserts'],
          highlights: [
            'Diagnoses the consolidation of seed, processing, and grocery markets',
            'Introduces the Ten Laws of Benevolent Food',
            'Prescribes the Food Pledge for farmers, retailers, policymakers, and society',
          ],
          link: 'https://www.amazon.com/dp/B0HHMD7SJ3',
        },
        {
          volume: 'XI',
          theme: 'theme-indigo',
          title: 'The Benevolent Workplace',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT WORKPLACE',
          subtitle: 'How Benevolism Would Transform Labor, Restore Worker Power, and Make Every Job a Source of Dignity and Prosperity',
          description: 'The worker is not a cost to be minimized — the worker is the economy. A full doctrine for living wages, collective bargaining, and gig worker rights that closes the forty-year gap between productivity and pay.',
          topics: ['Labor Rights', 'Living Wages', 'Collective Bargaining', 'Gig Economy'],
          highlights: [
            'Diagnoses the forty-year divergence between productivity growth and worker pay',
            'Introduces the Ten Laws of Benevolent Labor',
            'Prescribes the Worker\'s Pledge for employers, workers, policymakers, and society',
          ],
          link: 'https://www.amazon.com/dp/B0HHM7K9FP',
        },
        {
          volume: 'XII',
          theme: 'theme-rose',
          title: 'The Benevolent Marketplace',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT MARKETPLACE',
          subtitle: 'How Benevolism Would Transform Retail, Build Ethical Supply Chains, and Make Commerce Honest Again',
          description: 'Retail is the final mile where every other domain becomes a price tag. A full doctrine for honest markups, ethical sourcing, and retail labor that trades honestly at every link in the chain.',
          topics: ['Retail Pricing', 'Supply Chains', 'Fast Fashion', 'Ethical Sourcing'],
          highlights: [
            'Diagnoses the gap between what goods cost to make and what retailers charge',
            'Introduces the Ten Laws of Benevolent Retail',
            'Prescribes the Marketplace Pledge for retailers, workers, suppliers, and consumers',
          ],
          link: 'https://www.amazon.com/dp/B0HJH52F6P',
        },
        {
          volume: 'XIII',
          theme: 'theme-plum',
          title: 'The Benevolent Stage',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT STAGE',
          subtitle: 'How Benevolism Would Transform Entertainment, Restore Creator Rights, and Make Art Pay the People Who Make It',
          description: 'Culture is not a byproduct of the entertainment industry — it is the entire reason the industry exists. A full doctrine for honest royalties, creator ownership, and consent-based AI.',
          topics: ['Streaming Royalties', 'Creator Rights', 'Copyright Reform', 'AI & Consent'],
          highlights: [
            'Diagnoses a century of extractive deals across music, film, and publishing',
            'Introduces the Ten Laws of Benevolent Entertainment',
            'Prescribes the Creator\'s Pledge for platforms, studios, creators, and audiences',
          ],
          link: 'https://www.amazon.com/dp/B0HJFWHMVR',
        },
        {
          volume: 'XIV',
          theme: 'theme-forest',
          title: 'The Benevolent Ground',
          titleLine1: 'THE',
          titleLine2: 'BENEVOLENT GROUND',
          subtitle: 'How Benevolism Would Transform Real Estate Development, Build Community Wealth, and Make Land Work for the People Who Live on It',
          description: 'Land value is community value — created by public investment and collective activity, not by the owner alone. A full doctrine for community land trusts and land value capture that builds wealth instead of extracting it.',
          topics: ['Land Value', 'Community Land Trusts', 'Gentrification', 'Development Finance'],
          highlights: [
            'Diagnoses how real estate development extracts community-created land value',
            'Introduces the Ten Laws of Benevolent Real Estate',
            'Prescribes the Ground Pledge for developers, planners, investors, and residents',
          ],
          link: 'https://www.amazon.com/dp/B0HJFXKHBJ',
        },      ],
      /* ── COVENANT SERIES ────────────────────────────────────────
         International-scope application of Benevolism. Add more
         volumes here as new continents/regions are covered.
      ─────────────────────────────────────────────────────────── */
      covenantSeries: [
        {
          region: 'South America',
          title: 'The Covenant of the Continent',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for South America',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across South America paired with the Benevolism covenant redesign for each country, from the resource curse to sovereign debt.',
          topics: ['South America', 'Resource Extraction', 'Sovereign Debt', 'Indigenous Sovereignty'],
          highlights: [
            'Country-by-country covenant redesign for Brazil, Colombia, Argentina, Chile, Peru, Venezuela, Ecuador, Bolivia, Paraguay, Uruguay & the Guianas',
            'Documents the IMF, mining, agribusiness, and debt systems operating across the continent',
            'Proposes the Five Pillars of the Continental Covenant Architecture',
          ],
          cover: require('../assets/The Covenant Of The Continent.png'),
          link: 'https://www.amazon.com/dp/B0H825RK97',
        },
        {
          region: 'North America',
          title: 'A Covenant for the North',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for North America',
          description: 'The companion volume, applying the same documented indictment and covenant redesign to the United States, Canada, Mexico, Central America, and the Caribbean.',
          topics: ['United States', 'Canada', 'Mexico', 'Central America & the Caribbean'],
          highlights: [
            'Covers the military-industrial complex, health insurance, pharmaceutical pricing, and the prison system',
            'Country-by-country covenant redesign for the U.S., Canada, Mexico, Central America & the Caribbean',
            'Proposes the Five Pillars of the Covenant Architecture for the North',
          ],
          cover: require('../assets/A Covenant For The North.png'),
          link: 'https://www.amazon.com/dp/B0H83TSSCL',
        },
        {
          region: 'Africa',
          title: 'A Covenant for Africa',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Africa',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Africa, from the slave trade to illicit capital flight, paired with the Benevolism covenant redesign for each country.',
          topics: ['Africa', 'Colonial Extraction', 'Illicit Financial Flows', 'Resource Sovereignty'],
          highlights: [
            'Country-by-country covenant redesign for the DRC, Nigeria, South Africa, Ghana, Kenya, Ethiopia, the Sahel, Rwanda, and North Africa',
            'Documents the slave trade, colonial partition, debt traps, and ongoing illicit capital flight',
            'Proposes the Five Pillars of the Covenant Architecture for Africa',
          ],
          cover: require('../assets/A Covenant For Africa.png'),
          link: 'https://www.amazon.com/dp/B0H87DZPYZ',
        },
        {
          region: 'Europe',
          title: 'A Covenant for Europe',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Europe',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Europe, from colonial debt to austerity, paired with the Benevolism covenant redesign for each country.',
          topics: ['Europe', 'Colonial Debt', 'Austerity', 'Tax Havens'],
          highlights: [
            'Country-by-country covenant redesign for the UK, France, Germany, Greece, Ireland, Spain & Portugal, Italy, Eastern Europe, and the Nordic countries',
            'Documents the colonial debt, the EU austerity machine, arms exports, and European tax haven architecture',
            'Proposes the Five Pillars of the Covenant Architecture for Europe',
          ],
          cover: require('../assets/A Covenant For Europe.png'),
          link: 'https://www.amazon.com/dp/B0H8J4SKXZ',
        },
        {
          region: 'Asia',
          title: 'A Covenant for Asia',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Asia',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Asia, from supply chain exploitation to caste-based exclusion, paired with the Benevolism covenant redesign for each country.',
          topics: ['Asia', 'Supply Chains', 'Labor Rights', 'Climate Justice'],
          highlights: [
            'Country-by-country covenant redesign for India, Bangladesh, China, Pakistan, the Philippines, Myanmar, Cambodia, Vietnam, Indonesia, Japan, South Korea & Afghanistan',
            'Documents the fast fashion supply chain, the microfinance debt crisis, caste-based economic exclusion, and labor rights suppression',
            'Proposes the Five Pillars of the Covenant Architecture for Asia',
          ],
          cover: require('../assets/A Covenant For Asia.png'),
          link: 'https://www.amazon.com/dp/B0H8JKT77Y',
        },
        {
          region: 'Pacific',
          title: 'A Covenant for the Pacific',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Australia, New Zealand, and the Pacific Islands',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Australia, New Zealand, and the Pacific Islands, paired with the Benevolism covenant redesign for each country.',
          topics: ['Australia', 'New Zealand', 'Pacific Islands', 'Climate Justice'],
          highlights: [
            'Country-by-country covenant redesign for Australia, New Zealand, and the Pacific Island nations',
            'Documents the Stolen Generations, the Treaty of Waitangi framework, and Pacific climate vulnerability',
            'Proposes the Five Pillars of the Covenant Architecture for the Pacific',
          ],
          cover: require('../assets/A Covenant For The Pacific.png'),
          link: 'https://www.amazon.com/dp/B0H8KWFPS4',
        },
      ],
      books: [
        {
          title: 'Obsolescence: How Companies Use It To Get Wealthier',
          author: 'Enrico Michael Severe',
          cover: require('../assets/OBSOLESCENCE.png'),
          link: 'https://www.amazon.com/Obsolescense-How-Companies-Use-Wealthier/dp/B0FBHNMK3Y',
          description: 'An in-depth investigation into planned obsolescence and how corporations strategically design products to fail, driving endless consumption cycles and unprecedented wealth accumulation.',
          topics: ['Consumer Economics', 'Corporate Strategy', 'Product Design', 'Sustainability'],
          highlights: [
            'Examines the history and evolution of planned obsolescence',
            'Reveals corporate strategies behind product lifecycles',
            'Analyzes environmental and economic impacts',
          ],
          badge: 'Bestseller',
        },
        {
          title: 'The Prison Industrial Complex',
          author: 'Enrico Michael Severe',
          cover: require('../assets/THE PRISON.png'),
          link: 'https://www.amazon.com/Prison-Industrial-Complex-Comprehensive-Exploration/dp/B0FBHXBJ99',
          description: 'A comprehensive exploration of mass incarceration in America, exposing the profit-driven machinery behind the prison system and its devastating impact on communities.',
          topics: ['Criminal Justice', 'Mass Incarceration', 'Social Policy', 'Corporate Influence'],
          highlights: [
            'Traces the growth of the prison-industrial complex',
            'Investigates private prison profit models',
            'Documents social and economic consequences',
          ],
        },
        {
          title: 'Military Industrial Complex',
          author: 'Enrico Michael Severe',
          cover: require('../assets/Military Industrial Complex.png'),
          link: 'https://www.amazon.com/Military-Industrial-Complex-Power-Policy-Impact/dp/B0FG7ZHZ27',
          description: 'An authoritative analysis of the military-industrial complex, examining how defense contractors, government agencies, and political interests intersect to shape foreign policy and national priorities.',
          topics: ['Defense Policy', 'Government Contracting', 'Foreign Relations', 'Political Economy'],
          highlights: [
            'Maps the network of defense industry influence',
            'Analyzes policy impacts on global conflicts',
            'Explores economic dependencies on military spending',
          ],
        },
        {
          title: 'Plundered Republics: Corporate Imperialism In South America',
          author: 'Enrico Michael Severe',
          cover: require('../assets/Plundered Republics.png'),
          link: 'https://www.amazon.com/Plundered-Republics-Corporate-Imperialism-America/dp/B0FG81W37X',
          description: 'A powerful investigation into corporate exploitation across South America, documenting how multinational corporations have extracted resources and wealth while shaping political landscapes.',
          topics: ['Latin American History', 'Resource Extraction', 'Economic Imperialism', 'Political Influence'],
          highlights: [
            'Chronicles corporate activities across South America',
            'Reveals patterns of resource exploitation',
            'Examines long-term impacts on sovereignty and development',
          ],
        },
      ],
    }
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@400;600;700&family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@700;900&display=swap');

.books-container {
  min-height: 100vh;
  background: #0d1117;
}

/* ─── Hero ─────────────────────────────────────────────────── */
.books-hero {
  position: relative;
  min-height: 60vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 50%, rgba(201,168,76,0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(201,168,76,0.06) 0%, transparent 50%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  max-width: 900px;
  text-align: center;
  z-index: 1;
}

.hero-label {
  display: inline-block;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  color: #c9a84c;
  background: rgba(201,168,76,0.1);
  padding: 8px 20px;
  border-radius: 2px;
  margin-bottom: 24px;
  border: 1px solid rgba(201,168,76,0.3);
  animation: fadeInUp 0.8s ease-out;
}

.hero-title {
  font-family: 'Crimson Pro', serif;
  font-size: 64px;
  font-weight: 700;
  color: #f5f0e8;
  margin-bottom: 20px;
  letter-spacing: -1px;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.hero-description {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  line-height: 1.7;
  color: #c8c0b0;
  max-width: 800px;
  margin: 0 auto 48px;
  font-weight: 300;
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 48px;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

.stat-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 32px;
  background: rgba(201,168,76,0.08);
  border: 1px solid rgba(201,168,76,0.2);
}

.stat-number {
  font-family: 'Crimson Pro', serif;
  font-size: 40px;
  font-weight: 700;
  color: #c9a84c;
  line-height: 1;
}

.stat-text {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.15em;
}

/* ─── Benevolism Series Section ────────────────────────────── */
.series-section {
  background: #0b1628;
  border-top: 1px solid rgba(201,168,76,0.12);
  border-bottom: 1px solid rgba(201,168,76,0.12);
  padding: 72px 24px;
}

.series-inner { max-width: 1400px; margin: 0 auto; }

.series-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 48px;
  padding-bottom: 32px;
  border-bottom: 1px solid rgba(201,168,76,0.12);
  flex-wrap: wrap;
}

.series-header-left { display: flex; align-items: flex-start; gap: 20px; flex: 1; }

.series-mark {
  width: 52px;
  height: 52px;
  background: #c9a84c;
  color: #0b1628;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  font-size: 26px;
  flex-shrink: 0;
}

.series-mark--covenant { background: #d7a86e; color: #2c1810; }

.series-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #8a6e2a;
  margin-bottom: 6px;
}

.series-title {
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  font-weight: 700;
  color: #f5f0e8;
  margin: 0 0 12px;
}

.series-sub {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.65;
  color: #718096;
  font-weight: 300;
  max-width: 560px;
  margin: 0;
}

.series-vol-count { text-align: right; padding-left: 40px; border-left: 1px solid rgba(201,168,76,0.15); }
.series-vol-num { display: block; font-family: 'Playfair Display', serif; font-size: 48px; font-weight: 700; color: #c9a84c; line-height: 1; }
.series-vol-label { display: block; font-family: 'Inter', sans-serif; font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: #718096; margin-top: 4px; }

/* Series volume cards */
.series-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
  gap: 2px;
  margin-bottom: 0;
}

.series-card {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 32px;
  padding: 40px;
  background: #0d1827;
  border: 1px solid rgba(201,168,76,0.08);
  transition: border-color 0.2s, background 0.2s;
}

.series-card:hover { border-color: rgba(201,168,76,0.25); background: #111f33; }

/* CSS book cover for series */
.series-cover-wrap { position: relative; flex-shrink: 0; }

.series-cover {
  width: 120px;
  min-height: 175px;
  padding: 16px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 4px 4px 20px rgba(0,0,0,0.5);
  transform: rotate(-1.5deg);
  transition: transform 0.3s;
  position: relative;
}

.series-cover::before {
  content: '';
  position: absolute;
  inset: 5px;
  border: 1px solid rgba(201,168,76,0.18);
  pointer-events: none;
}

.series-cover:hover { transform: rotate(0deg) scale(1.03); }
.series-cover.theme-navy  { background: linear-gradient(160deg, #0d1f3c 0%, #0b1628 60%, #071020 100%); }
.series-cover.theme-emerald { background: linear-gradient(160deg, #123524 0%, #0e2a1c 60%, #061309 100%); }
.series-cover.theme-bronze { background: linear-gradient(160deg, #3d2e14 0%, #1b1409 60%, #0a0703 100%); }
.series-cover.theme-violet { background: linear-gradient(160deg, #422759 0%, #1f1130 60%, #0d0713 100%); }
.series-cover.theme-steel { background: linear-gradient(160deg, #1B4B59 0%, #0B242C 60%, #030D11 100%); }
.series-cover.theme-crimson { background: linear-gradient(160deg, #4A1420 0%, #2B0C13 60%, #120509 100%); }
.series-cover.theme-teal { background: linear-gradient(160deg, #0F3B3A 0%, #0A2726 60%, #041312 100%); }
.series-cover.theme-amber { background: linear-gradient(160deg, #4A2E0D 0%, #2E1B08 60%, #140C03 100%); }
.series-cover.theme-cyan { background: linear-gradient(160deg, #0D3548 0%, #082330 60%, #03121A 100%); }
.series-cover.theme-olive { background: linear-gradient(160deg, #33380F 0%, #20240A 60%, #0E1004 100%); }
.series-cover.theme-indigo { background: linear-gradient(160deg, #201A45 0%, #14112C 60%, #080614 100%); }
.series-cover.theme-rose { background: linear-gradient(160deg, #431722 0%, #2B0E16 60%, #12060A 100%); }
.series-cover.theme-plum { background: linear-gradient(160deg, #3A1240 0%, #240B29 60%, #0F0512 100%); }
.series-cover.theme-forest { background: linear-gradient(160deg, #2A2410 0%, #1A160A 60%, #0B0904 100%); }

.covenant-cover-img {
  width: 120px;
  min-height: 175px;
  object-fit: cover;
  display: block;
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 4px 4px 20px rgba(0,0,0,0.5);
  transform: rotate(-1.5deg);
  transition: transform 0.3s;
}
.covenant-cover-img:hover { transform: rotate(0deg) scale(1.03); }
.covenant-series-section .series-vol-tag { color: #d7a86e; }
.covenant-series-section .series-card:hover { border-color: rgba(215,168,110,0.3); }
.covenant-series-section .series-card-highlights { border-left-color: rgba(215,168,110,0.5); }

.sc-crown { font-size: 18px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.sc-title { font-family: 'Playfair Display', serif; font-size: 7.5px; color: #e8cc80; letter-spacing: 0.06em; line-height: 1.35; }
.sc-title strong { font-size: 10px; font-weight: 900; display: block; letter-spacing: 0.02em; margin-top: 2px; }
.sc-divider { width: 70%; height: 1px; background: linear-gradient(90deg, transparent, #c9a84c, transparent); }
.sc-scales { font-size: 15px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); }
.sc-series { font-family: 'Inter', sans-serif; font-size: 5.5px; letter-spacing: 0.08em; color: #c8c0b0; line-height: 1.4; }
.sc-author { font-family: 'Playfair Display', serif; font-size: 6.5px; font-weight: 700; color: #e8cc80; letter-spacing: 0.1em; }
.sc-heir { font-family: 'Inter', sans-serif; font-size: 5px; font-style: italic; color: #c8c0b0; }

.sc-badge {
  position: absolute;
  top: -10px; right: -10px;
  width: 36px; height: 36px;
  background: #c9a84c;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Playfair Display', serif;
  font-size: 7px;
  font-weight: 900;
  color: #0b1628;
  transform: rotate(15deg);
}

.series-card-content { display: flex; flex-direction: column; gap: 14px; }
.series-vol-tag { font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: #c9a84c; }
.series-card-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: #f5f0e8; margin: 0; line-height: 1.25; }
.series-card-sub { font-family: 'Playfair Display', serif; font-size: 13px; font-style: italic; color: #8a6e2a; margin: 0; line-height: 1.5; }
.series-card-desc { font-family: 'Inter', sans-serif; font-size: 13px; line-height: 1.65; color: #718096; font-weight: 300; margin: 0; }

.series-card-topics { display: flex; flex-wrap: wrap; gap: 6px; }
.series-topic-tag { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 500; color: #c8c0b0; background: rgba(255,255,255,0.05); border: 1px solid rgba(201,168,76,0.12); padding: 4px 10px; }

.series-card-highlights {
  background: rgba(201,168,76,0.04);
  border-left: 2px solid rgba(201,168,76,0.4);
  padding: 12px 16px;
}

.series-highlights-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.series-highlights-list li { display: flex; align-items: flex-start; gap: 8px; font-family: 'Inter', sans-serif; font-size: 12px; line-height: 1.55; color: #9ca3af; }
.series-highlights-list li svg { flex-shrink: 0; margin-top: 2px; color: #c9a84c; }

.series-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #0b1628;
  background: #c9a84c;
  padding: 11px 22px;
  text-decoration: none;
  align-self: flex-start;
  transition: background 0.2s;
}
.series-cta:hover { background: #e8cc80; }
.series-cta--soon { background: transparent; border: 1px solid rgba(201,168,76,0.35); color: #c8c0b0; cursor: default; }
.series-cta--soon:hover { background: transparent; }

.series-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 40px;
  margin-top: 0;
  border-top: 1px solid rgba(201,168,76,0.08);
  background: rgba(0,0,0,0.15);
  flex-wrap: wrap;
  gap: 12px;
}
.series-footer-note { font-family: 'Inter', sans-serif; font-size: 12px; color: #4b5563; font-style: italic; }
.series-footer-link { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600; color: #c9a84c; text-decoration: none; }
.series-footer-link:hover { color: #e8cc80; }

/* ─── Also Published ────────────────────────────────────────── */
.books-section {
  padding: 64px 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.also-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 48px;
}
.also-rule { flex: 1; height: 1px; background: #1f2937; }
.also-label { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.15em; color: #4b5563; white-space: nowrap; }

.books-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 32px;
}

.book-card {
  background: #132038;
  border: 1px solid rgba(201,168,76,0.1);
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  transition: all 0.4s cubic-bezier(0.4,0,0.2,1);
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 0;
  animation: fadeInUp 0.6s ease-out both;
}

.book-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  border-color: rgba(201,168,76,0.3);
}

.book-cover-wrapper {
  position: relative;
  padding: 28px;
  background: #0d1624;
}

.book-cover-container {
  position: relative;
  width: 100%;
  aspect-ratio: 2/3;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.4);
}

.book-cover-image { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
.book-card:hover .book-cover-image { transform: scale(1.05); }

.book-shine {
  position: absolute;
  top: 0; left: -100%;
  width: 50%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
  transition: left 0.5s ease;
}
.book-card:hover .book-shine { left: 100%; }

.book-badge {
  position: absolute;
  top: 40px; left: 20px;
  background: #c9a84c;
  color: #0b1628;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 5px 12px;
  z-index: 1;
}

.book-content {
  padding: 28px 28px 28px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.book-title {
  font-family: 'Crimson Pro', serif;
  font-size: 22px;
  font-weight: 700;
  color: #f5f0e8;
  line-height: 1.3;
  margin-bottom: 6px;
}

.book-author {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #c9a84c;
}

.book-description {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.7;
  color: #718096;
  font-weight: 300;
}

.book-topics { border-top: 1px solid rgba(201,168,76,0.1); padding-top: 14px; }

.topic-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #8a6e2a;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 10px;
}

.topics-list { display: flex; flex-wrap: wrap; gap: 6px; }

.topic-tag {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #c8c0b0;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(201,168,76,0.1);
  padding: 5px 12px;
  transition: all 0.2s;
}
.topic-tag:hover { background: rgba(201,168,76,0.1); color: #c9a84c; border-color: rgba(201,168,76,0.3); }

.book-highlights {
  background: rgba(201,168,76,0.04);
  border-left: 2px solid rgba(201,168,76,0.4);
  padding: 14px 16px;
}

.highlights-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.highlights-list li { display: flex; align-items: flex-start; gap: 10px; font-family: 'Inter', sans-serif; font-size: 12px; line-height: 1.6; color: #9ca3af; }
.highlights-list li svg { flex-shrink: 0; margin-top: 2px; color: #c9a84c; }

.book-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(201,168,76,0.1);
  border: 1px solid rgba(201,168,76,0.3);
  color: #c9a84c;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.06em;
  align-self: flex-start;
  transition: all 0.2s;
}
.book-cta:hover { background: #c9a84c; color: #0b1628; gap: 12px; }

/* ─── Author Bio ────────────────────────────────────────────── */
.author-bio-section {
  background: #111827;
  border-top: 1px solid rgba(201,168,76,0.1);
  padding: 72px 24px;
}

.bio-content { max-width: 900px; margin: 0 auto; text-align: center; }

.bio-icon { margin: 0 auto 28px; color: #c9a84c; }

.bio-title {
  font-family: 'Crimson Pro', serif;
  font-size: 44px;
  font-weight: 700;
  color: #f5f0e8;
  margin-bottom: 28px;
  letter-spacing: -1px;
}

.bio-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 1.8;
  color: #9ca3af;
  margin-bottom: 20px;
  text-align: left;
  font-weight: 300;
}

.bio-cta { margin-top: 36px; display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }

.bio-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 13px 26px;
  background: transparent;
  color: #c8c0b0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid rgba(255,255,255,0.15);
  transition: all 0.2s;
}
.bio-link:hover { border-color: rgba(255,255,255,0.35); color: #f5f0e8; }

.bio-link--gold { color: #c9a84c; border-color: rgba(201,168,76,0.3); }
.bio-link--gold:hover { background: #c9a84c; color: #0b1628; border-color: #c9a84c; }

/* ─── CTA / Newsletter ──────────────────────────────────────── */
.newsletter-section {
  background: #0b1628;
  border-top: 1px solid rgba(201,168,76,0.12);
  padding: 72px 24px;
}

.newsletter-content { max-width: 800px; margin: 0 auto; text-align: center; }
.newsletter-icon { margin: 0 auto 28px; color: #c9a84c; }

.newsletter-title {
  font-family: 'Crimson Pro', serif;
  font-size: 48px;
  font-weight: 700;
  color: #f5f0e8;
  margin-bottom: 16px;
  letter-spacing: -1px;
}

.newsletter-description {
  font-family: 'Inter', sans-serif;
  font-size: 17px;
  line-height: 1.65;
  color: #718096;
  margin-bottom: 40px;
  font-weight: 300;
}

.newsletter-buttons { display: flex; justify-content: center; gap: 14px; flex-wrap: wrap; }

.newsletter-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
  letter-spacing: 0.04em;
}

.newsletter-btn.primary {
  background: #1c2e4a;
  color: #c8c0b0;
  border: 1px solid rgba(201,168,76,0.15);
}
.newsletter-btn.primary:hover { background: #243d60; color: #f5f0e8; }

.newsletter-btn.gold {
  background: #c9a84c;
  color: #0b1628;
}
.newsletter-btn.gold:hover { background: #e8cc80; }

.newsletter-btn.secondary {
  background: transparent;
  color: #718096;
  border: 1px solid rgba(255,255,255,0.1);
}
.newsletter-btn.secondary:hover { border-color: rgba(255,255,255,0.25); color: #c8c0b0; }

/* ─── Responsive ────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .books-grid { grid-template-columns: 1fr; }
  .book-card { grid-template-columns: 1fr; }
  .book-cover-wrapper { padding: 28px 28px 0; }
  .book-cover-container { max-width: 260px; margin: 0 auto; }
  .book-content { padding: 20px 28px 28px; }
  .series-grid { grid-template-columns: 1fr; }
  .series-card { grid-template-columns: 130px 1fr; }
}

@media (max-width: 768px) {
  .hero-title { font-size: 42px; }
  .hero-description { font-size: 16px; }
  .hero-stats { gap: 16px; }
  .stat-box { padding: 14px 20px; }
  .series-header { flex-direction: column; }
  .series-vol-count { border-left: none; border-top: 1px solid rgba(201,168,76,0.15); padding: 16px 0 0; text-align: left; }
  .series-card { grid-template-columns: 1fr; }
  .series-footer { flex-direction: column; padding: 16px 24px; }
  .newsletter-title { font-size: 36px; }
  .newsletter-buttons { flex-direction: column; }
  .newsletter-btn { justify-content: center; }
  .bio-title { font-size: 34px; }
}
</style>